import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { equipmentData } from '../mock';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const GeneradoresPage = () => {
  const { generators } = equipmentData;

  return (
    <>
      <Helmet>
        <title>Generadores Eléctricos en Renta | GENSOL Veracruz - 5kW a 1000kW</title>
        <meta name="description" content="Renta de generadores eléctricos desde 5kW hasta 1000kW. Generadores silenciosos, portátiles e industriales para eventos, construcción y emergencias en Veracruz." />
        <meta name="keywords" content="generadores eléctricos renta, generador 50kW, generador 100kW, generador 500kW, planta de luz, Veracruz" />
        <link rel="canonical" href="https://www.gensolver.com/generadores" />
        <meta property="og:title" content="Generadores Eléctricos en Renta | GENSOL" />
        <meta property="og:description" content="Amplia gama de generadores desde 5kW hasta 1000kW. Silenciosos, eficientes y con soporte 24/7." />
        <meta property="og:url" content="https://www.gensolver.com/generadores" />
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Generadores', url: 'https://www.gensolver.com/generadores' },
        ]}
      />

      <main id="generadores" data-page="generadores" data-analytics-page="generadores" className="pt-24 min-h-screen bg-gray-200">
        {/* Page Header */}
        <section className="py-12 bg-gray-200">
          <div className="container mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-orange-500 font-semibold">Catálogo de Equipos</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Generadores <span className="text-orange-500">Eléctricos</span>
            </h1>
            <p className="text-gray-800 max-w-2xl mx-auto">
              Desde pequeños generadores portátiles hasta plantas industriales de alta potencia
            </p>
          </div>
        </section>

        {/* Generators Grid */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
              {generators.map((generator) => (
                <div 
                  key={generator.id}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-orange-500/50 transition-all duration-300 group"
                  data-testid={`generator-card-${generator.id}`}
                  data-analytics-element={`generator-${generator.power.replace(/\s/g, '-')}`}
                  data-analytics-category="generators"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={generator.image} 
                      alt={`${generator.name} - GENSOL Veracruz`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-orange-500 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                      {generator.power}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                  </div>
                  
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{generator.name}</h2>
                    <p className="text-gray-600 mb-4">{generator.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      {generator.features.map((feature, idx) => (
                        <span 
                          key={feature}
                          className="bg-orange-500/10 text-orange-500 px-3 py-1 rounded-full text-sm border border-orange-500/30"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <Link 
                      to="/contacto"
                      data-testid={`generator-quote-btn-${generator.id}`}
                      data-analytics-click={`quote-generator-${generator.power.replace(/\s/g, '-')}`}
                      className="block w-full bg-orange-500 hover:bg-orange-600 text-gray-900 text-center py-3 rounded-lg font-semibold transition-all hover:scale-[1.02]"
                    >
                      Cotizar Este Generador
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Specifications Table */}
        <section className="py-16 bg-gray-200">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-black text-center mb-12">
              Especificaciones <span className="text-orange-500">Técnicas</span>
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-analytics-element="specs-table">
                <thead>
                  <tr className="border-b border-orange-500/30">
                    <th className="py-4 px-6 text-orange-500 font-semibold">Categoría</th>
                    <th className="py-4 px-6 text-orange-500 font-semibold">Potencia</th>
                    <th className="py-4 px-6 text-orange-500 font-semibold">Uso Ideal</th>
                    <th className="py-4 px-6 text-orange-500 font-semibold">Combustible</th>
                  </tr>
                </thead>
                <tbody className="text-black">
                  <tr className="border-b border-gray-300 hover:bg-white/60">
                    <td className="py-4 px-6">Portátiles</td>
                    <td className="py-4 px-6">5 - 50 kW</td>
                    <td className="py-4 px-6">Eventos, Construcción residencial</td>
                    <td className="py-4 px-6">Diesel / Gasolina</td>
                  </tr>
                  <tr className="border-b border-gray-300 hover:bg-white/60">
                    <td className="py-4 px-6">Medianos</td>
                    <td className="py-4 px-6">50 - 250 kW</td>
                    <td className="py-4 px-6">Comercios, Industria ligera</td>
                    <td className="py-4 px-6">Diesel</td>
                  </tr>
                  <tr className="border-b border-gray-300 hover:bg-white/60">
                    <td className="py-4 px-6">Industriales</td>
                    <td className="py-4 px-6">250 - 1000 kW</td>
                    <td className="py-4 px-6">Fábricas, Plantas de producción</td>
                    <td className="py-4 px-6">Diesel</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-black mb-4">
              ¿No encuentras lo que buscas?
            </h2>
            <p className="text-gray-800 mb-8">
              Contáctanos y te ayudamos a encontrar el generador perfecto para tu proyecto
            </p>
            <Link 
              to="/contacto"
              data-testid="generators-cta-contact"
              data-analytics-click="generators-page-contact"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-gray-900 px-8 py-4 rounded-lg font-semibold transition-all hover:scale-105"
            >
              Contactar a un Experto
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default GeneradoresPage;
